import React, { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Alert, AlertDescription, AlertIcon, InfoWorkZero, Tag, toast } from '@mesh.ai/rabbit';
import { useVisierSyncStatus } from 'Deer/hooks/useVisierSyncStatus';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { updateEmbeddedAnalyticsStatus } from 'reducers/embeddedAnalytics';
import { updateVisierGlobals, visierIframeId, visierSDKScript } from 'screens/EmbeddedAnalytics/visierSDKScript';
import { embeddedAnalyticsSelector } from 'selectors/embeddedAnalyticsSelector';

import NoGraphData from 'assets/assetsV1/illustrations/newIllustrations/noDataGraph.svg';
import MeshMavenLoading from 'assets/gifs/meshMavenLoading.gif';

import {
  COMPANY_VISIER_ANALYSIS,
  TEAM_DIRECT_VISIER_ANALYSIS,
  TEAM_SKIP_VISIER_ANALYSIS,
  VISIER_DATA_EXPORT_ROOMS,
  VISIER_NAME_MAPPING,
} from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';
import {
  sendNavigatedToCompetenciesInsightsEvent,
  sendNavigatedToEngagementInsightsEvent,
  sendNavigatedToGoalsInsightsEvent,
  sendNavigatedToOneOnOneInsightsEvent,
  sendNavigatedToReportsEvent,
  sendNavigatedToReviewsInsightsEvent,
} from 'constants/mixpanelHelper';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextHeader2, TextBodyMedium } from 'uiToolkitV1/Typography/Typography';

const VisierAnalytics = ({ id }: { id: string }) => {
  const { data: visierSyncStatus } = useVisierSyncStatus();
  const [loading, setLoading] = useState(true);
  const [loadingFailed, setLoadingFailed] = useState({ status: false, msg: null });
  const dispatch = useDispatch();
  const { embeddedAnalyticsStatus: organizationStatus } = useSelector(embeddedAnalyticsSelector);

  const getVisierLandingURL = (): string => {
    if (id === 'reports' || id === 'outcomesDashboard') {
      sendNavigatedToReportsEvent({});
      return VISIER_DATA_EXPORT_ROOMS[id];
    }

    const allAnalysis = [...COMPANY_VISIER_ANALYSIS, ...TEAM_DIRECT_VISIER_ANALYSIS, ...TEAM_SKIP_VISIER_ANALYSIS];
    const analysis = allAnalysis.find((item) => item.id === id);

    switch (analysis.name) {
      case VISIER_NAME_MAPPING.ANALYZING_GOAL_ACHIEVEMENT:
        sendNavigatedToGoalsInsightsEvent({});
        break;
      case VISIER_NAME_MAPPING.ANALYZING_PERFORMANCE:
        sendNavigatedToReviewsInsightsEvent({});
        break;
      case VISIER_NAME_MAPPING.ANALYZING_MANAGER_1_1S:
        sendNavigatedToOneOnOneInsightsEvent({});
        break;
      case VISIER_NAME_MAPPING.ANALYZING_COMPETENCIES:
        sendNavigatedToCompetenciesInsightsEvent({});
        break;
      case VISIER_NAME_MAPPING.ANALYZING_ENGAGEMENT:
        sendNavigatedToEngagementInsightsEvent({});
        break;
      default:
        break;
    }

    if (analysis?.id) {
      return analysis.url;
    }
    return '';
  };

  const handleAutoprovisionSuccess = () =>
    toast.success('Autoprovision Successful', { description: 'User successfully auto-provisioned!' });

  const handleEvents = (msg) => {
    switch (msg?.code?.toUpperCase()) {
      case 'SESSION_ESTABLISHED':
        setLoading(false);
        break;
      case 'USER_AUTO_PROVISION_SUCCESS':
        handleAutoprovisionSuccess();
        break;
      case 'AUTO_LOGIN_FAILED':
        setLoading(false);
        setLoadingFailed({ status: true, msg });
        break;
      case 'VISIER_APP_LOADED':
        setLoading(false);
        break;
      case 'SESSION_ERROR':
        setLoading(false);
        setLoadingFailed({ status: true, msg });

        break;
      default:
        break;
    }
  };

  const handleVisierErrors = (message, data) => {
    if (organizationStatus === 'PENDING') {
      setLoadingFailed({ status: true, msg: { code: '' } });
    } else if (organizationStatus !== 'SETUP') {
      toast.error(message, { description: data });
    }

    setLoading(false);
  };

  useEffect(() => {
    const landingURL = getVisierLandingURL();

    fetch(endpoints.embeddedAnalytics.connect, { credentials: 'include' })
      .then((res) => res.json())
      .then((res) => {
        if (res?.error) {
          dispatch(updateEmbeddedAnalyticsStatus(res?.status === 'FORBIDDEN' ? 'SETUP' : 'FAILED'));
          return;
        }
        dispatch(updateEmbeddedAnalyticsStatus(res?.entity?.organizationStatus));
      })
      .catch((err) => {
        console.error('Failed to connect', err);
        dispatch(updateEmbeddedAnalyticsStatus('FAILED'));
      });

    type VisierWrapperProps = Window & { embeddingApp: { embedApp: (arg1: string, arg2?: string) => void } };

    if ((window as unknown as VisierWrapperProps).embeddingApp) {
      updateVisierGlobals({ eventsCallback: handleEvents, errorCallback: handleVisierErrors });

      try {
        (window as unknown as VisierWrapperProps).embeddingApp.embedApp(visierIframeId, landingURL);
      } catch (error) {
        console.log('Error in embeddingApp', error);
      }
    } else {
      visierSDKScript(handleEvents, handleVisierErrors, landingURL);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingFailed.status) {
    return (
      <Box height="100%" display="flex" justifyContent="center" alignItems="center">
        <EmptyScreen
          heading={
            organizationStatus === 'SETUP' ? 'Analytics and insights are not enabled' : 'Failed to load analytics data'
          }
          description={
            organizationStatus === 'SETUP'
              ? 'Please contact customer support to enable analytics and insights.'
              : `We are unable to load the analytics data. Please contact customer support. ${loadingFailed.msg?.code?.toUpperCase()}`
          }
          illustrationSrc={NoGraphData}
        />
      </Box>
    );
  }

  return (
    <Box padding={loading ? '8px 16px' : ''}>
      {loading && (
        <div
          style={{
            width: '100%',
            height: 'calc(100vh - 50px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img src={MeshMavenLoading} alt="Logo" width="240px" height="240px" />
          <TextHeader2 bold style={{ marginTop: '10px' }}>
            Loading analytics data
          </TextHeader2>
          <TextBodyMedium>Please wait...</TextBodyMedium>
        </div>
      )}
      {organizationStatus === 'SANDBOX' && (
        <Box mb="8px">
          <Alert variant="warning">
            <AlertIcon IconSVG={InfoWorkZero} />
            <AlertDescription>
              This is static data for demo purposes only. Any updates will not reflect on analytics.
            </AlertDescription>
          </Alert>
        </Box>
      )}
      {visierSyncStatus && !loading && !loadingFailed.status ? (
        <Box w="fit-content" my="4px">
          <Tag colorScheme="tag-lightGray">
            {`Last updated on ${format(
              new Date(`${visierSyncStatus?.lastSyncTime}Z`),
              'PPPppp'
            )}. Next update scheduled on ${format(new Date(`${visierSyncStatus?.nextSyncTime}Z`), 'PPPppp')}`}
          </Tag>
        </Box>
      ) : null}
      <iframe
        title="v-app"
        id={visierIframeId}
        style={{
          width: '100%',
          height: 'calc(100vh - 128px)',
          display: 'none',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
        }}
      />
    </Box>
  );
};

export { VisierAnalytics };
